"use client";

import { useTranslation } from "react-i18next";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@packages/ui/src/components/shadcn/table";
import ContainerGlob from "../../../../componentsGLob/ContainerGlob";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faStar, faTimes } from "@fortawesome/free-solid-svg-icons";

const planColors = ["#2a7efd", "#fa2b35", "#fdc700"];

const FeatureCell = ({ value }: { value: string }) => {
  const { t } = useTranslation("buzzCard");

  switch (value) {
    case "FREE":
      return (
        <span className="font-bold text-green-600">{t("table.free")}</span>
      );
    case "check":
      return <FontAwesomeIcon icon={faCheck} className="text-green-500" />;
    case "kit":
      return (
        <span className="font-semibold">
          <FontAwesomeIcon icon={faStar} className="text-yellow-500 mr-1" />
          {t("table.completeKit")}
        </span>
      );
    case "check_friends":
      return (
        <span>
          <FontAwesomeIcon icon={faCheck} className="text-green-500 mr-1" />
          {t("table.plusTwoFriends")}
        </span>
      );
    case "no":
      return <FontAwesomeIcon icon={faTimes} className="text-red-500" />;
    case "STANDARD_COST":
      return t("table.standardCost");
    case "FREE_OR_SPLIT":
      return t("table.freeOrSplit");
    default:
      return <>{value}</>;
  }
};

export default function ComparisonTable() {
  const { t } = useTranslation("buzzCard");

  const tableHeaders = [
    t("table.headers.advantages"),
    t("table.headers.noBuzz"),
    t("table.headers.workerBee"),
    t("table.headers.foragerBee"),
    t("table.headers.queenBee"),
  ];

  const tableRows = [
    {
      feature: t("table.features.dailyPass"),
      values: ["10€", "5€", "5€", "5€"],
    },
    {
      feature: t("table.features.voucher10Pass"),
      values: ["90€", "45€", "45€", "45€"],
    },
    {
      feature: t("table.features.monthlyPass"),
      values: ["120€", "60€", "60€", "60€"],
    },
    {
      feature: t("table.features.equipment"),
      values: ["15€", "5€", "5€", "FREE"],
    },
    {
      feature: t("table.features.coffee"),
      values: ["4€", "2€", "FREE", "FREE"],
    },
    {
      feature: t("table.features.exclusiveGadget"),
      values: ["no", "check", "check", "kit"],
    },
    {
      feature: t("table.features.communityEvents"),
      values: [
        "STANDARD_COST",
        "FREE_OR_SPLIT",
        "FREE_OR_SPLIT",
        "FREE_OR_SPLIT",
      ],
    },
    {
      feature: t("table.features.privilegedAccess"),
      values: ["no", "no", "check", "check"],
    },
    {
      feature: t("table.features.discountsPartnerships"),
      values: ["no", "no", "no", "check"],
    },
    {
      feature: t("table.features.birthdayAccess"),
      values: ["no", "no", "check_friends", "check_friends"],
    },
    {
      feature: t("table.features.wagglePoints"),
      values: ["no", "check", "check", "check"],
    },
  ];

  return (
    <section className="w-full px-6">
      <ContainerGlob>
        <h2 className="text-3xl md:text-4xl font-semibold mb-6 text-center">
          {t("table.title")}
        </h2>

        {/* Desktop */}
        <div className="hidden md:block overflow-x-auto rounded-lg border bg-white shadow-sm">
          <Table className="w-full min-w-[900px] table-fixed">
            <TableHeader className="sticky top-0 bg-white z-10">
              <TableRow>
                {tableHeaders.map((header, index) => (
                  <TableHead
                    key={index}
                    className={`${
                      index === 0
                        ? "text-left w-1/4 px-3 py-3"
                        : "text-center w-[18%] px-2 py-3"
                    }`}
                    style={{
                      color: index > 1 ? planColors[index - 2] : "inherit",
                    }}
                  >
                    {header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {tableRows.map((row, rowIndex) => (
                <TableRow key={rowIndex} className="border-b last:border-b-0">
                  <TableCell className="font-medium text-left px-3 py-3 break-words">
                    {row.feature}
                  </TableCell>
                  {row.values.map((value, valueIndex) => (
                    <TableCell
                      key={valueIndex}
                      className="text-center px-2 py-3 break-words"
                    >
                      <FeatureCell value={value} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Mobile */}
        <div className="md:hidden space-y-8">
          {tableHeaders.slice(2).map((plan, i) => (
            <div
              key={plan}
              className="rounded-xl bg-white p-5 shadow-sm border"
              style={{ borderColor: planColors[i] }}
            >
              <h3
                className="text-xl font-semibold mb-4 text-center"
                style={{ color: planColors[i] }}
              >
                {plan}
              </h3>
              <ul className="space-y-3 text-sm">
                {tableRows.map((row, rowIndex) => (
                  <li
                    key={rowIndex}
                    className="flex justify-between items-start"
                  >
                    <span className="text-gray-500 break-words w-1/2">
                      {row.feature}
                    </span>
                    <span className="font-medium text-right">
                      <FeatureCell value={row.values[i + 1]} />
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </ContainerGlob>
    </section>
  );
}
